import { isEmpty } from 'lodash';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';

import { HeroBanner, TravelBalanceSummary } from 'components';
import { useFetch, useUser } from 'hooks';
import { travelBalanceRoutes } from 'router/appRoutes/travelBalanceRoutes';
import { appService } from 'services';

import travelHero from 'assets/img/travel-hero.png';

export const BalanceContext = createContext(null);

const TravelBalancePage = () => {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const {
    call: travelPageCall,
    data: travelPageResponse,
    loading: loadingTravelPage
  } = useFetch({ filterUserCompany: true });
  const { call: travelOrderCall, loading: orderLoading, data: travelOrder } = useFetch();
  const { currentUser } = useUser();
  const isConfiramtionStep = location.pathname === travelBalanceRoutes.travelBalance.children.confirmation.path;
  const travelPage = travelPageResponse?.data?.[0];

  const heroData = useMemo(() => {
    if (isEmpty(travelPage)) {
      // Display default hero image and title if there are no content in Strapi
      return {
        heroTitle: t('yourTravel'),
        heroImageDesktop: { frontUrl: travelHero },
        heroImageMobile: { frontUrl: travelHero }
      };
    } else {
      return travelPage;
    }
  }, [travelPage]);
  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getTravelOrder();
    getTravelPageData();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getTravelOrder = () => {
    if (!currentUser?.serialNumber || !searchParams.get('orderId')) return;
    travelOrderCall({
      service: 'castelis',
      path: `order/${currentUser.serialNumber}/travelOrderDetail/${searchParams.get('orderId')}`,
      method: 'get'
    });
  };

  const getTravelPageData = async () => {
    travelPageCall({
      service: appService.getStrapiServiceName(),
      method: 'get',
      path: '/api/purchase-pages'
    });
  };

  return (
    <BalanceContext.Provider value={{ travelOrder, orderLoading }}>
      <HeroBanner
        title={heroData?.heroTitle}
        image={heroData?.heroImageDesktop}
        mobileImage={heroData?.heroImageMobile}
        loading={loadingTravelPage}
      />
      <div className="main-container bg-gray-100 py-20">
        <div className="flex flex-col lg:flex-row lg:gap-x-8">
          <div className="lg:grow">
            <Outlet />
          </div>

          {!isConfiramtionStep && (
            <div className="order-first lg:order-last lg:shrink-0 lg:w-96 lg:max-w-64 xl:max-w-sm">
              <TravelBalanceSummary />
            </div>
          )}
        </div>
      </div>
    </BalanceContext.Provider>
  );
};

export const useBalanceContext = () => useContext(BalanceContext);

export default TravelBalancePage;
